import { Injectable, Injector, NgZone } from '@angular/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { ConfigService } from '../config/config.service';
import { WebcontentService } from '../webcontent/webcontent.service';
import { Navigator } from 'src/app/services/routing/navigator';
import { RedirectService } from '../redirect/redirect.service';
import { UserSessionService } from '../user-session/user-session.service';
import { LoadingController } from '@ionic/angular';
import { map, take } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RoutingService } from '../routing/routing.service';
import { ControlService } from '../control/control.service';
import { StructureService } from '../structure/structure.service';
import { CategoryInterface } from 'src/app/interfaces/lobby.interface';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class DeeplinkRedirectService {
  private isAuthenticated: boolean = false;
  private loading: any;

  constructor(
    private config: ConfigService,
    private loadingController: LoadingController,
    private navigator: Navigator,
    private redirectService: RedirectService,
    private routing: RoutingService,
    private userSessionService: UserSessionService,
    private zone: NgZone,
    private controlService: ControlService,
    private structure: StructureService
  ) {
    this.userSessionService.sessionToken$
      .pipe(
        map(token => !!token),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.isAuthenticated = this.userSessionService.isAuthenticated();
      });
  }

  public deeplinksListener() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const url = event.url;
        console.log("url", url);
        if (url) {
          this.checkForRedirects(url);
        }
      });
    });
  }

  private async checkForRedirects(deeplinkUrl) {

    if (deeplinkUrl.indexOf('#password_forgot') > -1 || deeplinkUrl.indexOf('#password_reset') > -1) {
      let twoFactor = this.config.getConfig().FEATURE_MFA_ON_EMAIL ?? 0;

      if (twoFactor == 1 || twoFactor == "1") {
        return this.navigator.navigateTo("ResetPassword2FAComponent", {}, { tokenURL: deeplinkUrl });
      } else {
        return this.navigator.navigateTo("ResetPassword2FAComponent", {}, { tokenURL: deeplinkUrl });
      }
    } else if (deeplinkUrl.includes('#game_launch')) {
      this.redirectService.gameLaunch(deeplinkUrl);
    } else {
      if (!deeplinkUrl.includes('onelink.me')) {
        const { pathname } = new URL(deeplinkUrl);
        const [, stub, param] = pathname.split("/");
        this.loading = await this.loadingController.create({
          message: 'Loading...',
          duration: 2000
        });

        this.loading.present();
        setTimeout(() => {
          this.deeplinkAction(stub, param);
          this.loading.dismiss();
        }, 2000);
      }
    }
  }

  private deeplinkAction(tabName: string, urlParam?: string): void {
    console.log("deeplinkAction", tabName, urlParam);

    switch (tabName) {
      case 'article':
        this.handlePromotionRedirect(urlParam);
        break;
      case 'promotions':
        this.handlePromotionRedirect(tabName);
        break;

      case 'livedealer':
        this.handleLiveDealerRedirect();
        break;

      case 'live-dealer':
        this.handleLiveDealerRedirect();
        break;

      case 'closevip':
        break; //do nothing, stay on the current page

      case 'signupstepone':
        if (!this.isAuthenticated) {
          this.navigator.navigateTo('SignupStepOneComponent');
        }
        break;

      case 'login':
        if (!this.isAuthenticated) {
          this.navigator.navigateTo('LoginComponent');
        }
        break;

      case 'casino':
        this.handleCasinoRedirect(urlParam);
        break;
      default:
        this.handleCasinoRedirect();
        break;
    }
  }

  private handlePromotionRedirect(urlParam?: string): void {
    if (urlParam === 'promotions' || urlParam === 'promos') {
      this.routing.toPromotionsTab();
    } else {
      this.routing.toWebviewTab(false, urlParam);
    }
  }

  private handleLiveDealerRedirect(): void {
    this.routing.toLiveDealerTab();
  }

  private handleCasinoRedirect(urlParam?: string): void {
    this.structure.getCategoriesByPlacement('MAIN')
      .pipe(
        map(categories => categories?.filter(item => !item.hide_category)?.find(item => item.category_route?.toLowerCase() === urlParam.toLowerCase())),
        take(1),
        untilDestroyed(this)
      )
      .subscribe((category: CategoryInterface | undefined) => {
        if (category) {
          this.controlService.selectCategory(category);
        }
      });
  }

}